import type { ContractVersionPageContractVersionFragment } from 'generated/resources/graphql'
import { minBy, orderBy } from 'lodash'
import moment, { Moment } from 'moment'

import { stringToDate } from 'modules/core/lib/dates'

type OrderType = 'desc' | 'asc'

class GraphqlContract {
  startsOn: Moment
  endsOn: Moment | null
  versions: ContractVersionPageContractVersionFragment[]

  constructor(startsOn: string | Moment) {
    this.startsOn = moment(startsOn)
    this.endsOn = null
    this.versions = []
  }

  sortedVersions(order: OrderType = 'desc') {
    return orderBy(this.versions, ['effectiveOn'], [order])
  }

  effectiveOn() {
    const minEffectiveOnVersion = minBy(this.versions, 'effectiveOn')
    const effectiveOnDate = stringToDate(minEffectiveOnVersion?.effectiveOn ?? '', 'backend')

    if (!effectiveOnDate) return null
    if (!effectiveOnDate.isValid()) return null

    return effectiveOnDate
  }

  pushVersion(version: ContractVersionPageContractVersionFragment) {
    this.versions.push(version)
  }

  includesVersion(version: ContractVersionPageContractVersionFragment): boolean {
    return !!this.versions.find(({ id }) => id === version.id)
  }

  formattedStartOn(format = 'MMM Do YYYY'): string {
    return this.startsOn.format(format)
  }

  hasEndDate(): boolean {
    return this.endsOn !== null
  }

  isOngoing(): boolean {
    const now = moment()
    const targetDate = this.effectiveOn() || this.startsOn
    return !targetDate.isAfter(now) && !this.hasExpired()
  }

  hasExpired(): boolean {
    const now = moment()
    return this.endsOn !== null && this.endsOn.isBefore(now)
  }

  isUpcoming(): boolean {
    const now = moment()
    const targetDate = this.effectiveOn() ?? this.startsOn
    return targetDate.isAfter(now)
  }
}

export default GraphqlContract
